import { CopyButton, PageHead } from '../ui';

const base = 'https://api.imaginai.live';

// A code block you can take in one click. Every one of these is meant to be
// pasted into a terminal, and selecting a wrapped multi-line curl by hand is
// how a stray character ends up in it.
function Snippet({ children }: { children: string }) {
  return (
    <div className="snippet">
      <pre>{children}</pre>
      <CopyButton text={children} label="Copy" className="secondary small" />
    </div>
  );
}

// Kept short on purpose: four endpoints, the signature scheme, and the errors
// that mean something. A reference nobody can hold in their head gets skimmed,
// and the parts that get skimmed here are the ones that cost money.
export default function Docs() {
  return (
    <>
      <PageHead
        title="Using the API"
        lead="The endpoints, the signature scheme, and the errors worth handling. Everything else is in the specification."
      />

      <div className="panel">
        <h3>Authentication</h3>
        <p>
          Every request carries your key as a bearer token. A key that is revoked, or used from an
          address outside its allowlist, is refused with <span className="mono">401</span>.
        </p>
        <Snippet>{`Authorization: Bearer sk_live_...`}</Snippet>
      </div>

      <div className="panel">
        <h3>Start a generation</h3>
        <p>
          Multipart, with the template id and the source image. Your balance is charged before the
          provider is asked, and refunded automatically if anything after that fails.
        </p>
        <Snippet>{`curl -X POST ${base}/v1/generations \\
  -H "Authorization: Bearer $KEY" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -F template_id=tpl_123 \\
  -F quality=high \\
  -F duration_seconds=8 \\
  -F file=@photo.jpg`}</Snippet>
        <p className="muted small">
          <span className="mono">quality</span> (<span className="mono">medium</span> or{' '}
          <span className="mono">high</span>) and <span className="mono">duration_seconds</span>{' '}
          (<span className="mono">5</span>, <span className="mono">8</span>, or <span className="mono">12</span>) apply to templates
          that list <span className="mono">video_options</span> in the catalog, each with its price.
          Leave them out for medium, 5 seconds. A template with{' '}
          <span className="mono">requires_prompt</span> takes a <span className="mono">prompt</span>{' '}
          field as well.
        </p>
        <p className="muted small">
          <span className="mono">Idempotency-Key</span> is optional and worth sending: a retry after
          a timeout returns the generation the first call created rather than starting — and
          charging for — a second one. The first call answers{' '}
          <span className="mono">202</span>; a repeat answers <span className="mono">200</span> with
          that generation as it stands, so a retry after it finished carries the{' '}
          <span className="mono">result_url</span> too.
        </p>
      </div>

      <div className="panel">
        <h3>Collect the result</h3>
        <p>
          Poll the generation, or let the webhook tell you. Status runs{' '}
          <span className="mono">PENDING → QUEUED → PROCESSING → DONE</span>, and{' '}
          <span className="mono">result_url</span> appears with <span className="mono">DONE</span>.
        </p>
        <Snippet>{`curl ${base}/v1/generations/348517 \\
  -H "Authorization: Bearer $KEY"`}</Snippet>
      </div>

      <div className="panel">
        <h3>Catalog, usage and account</h3>
        <Snippet>{`GET ${base}/v1/catalog/items      # what can be generated, and the price of each
GET ${base}/v1/usage              # spend, refunds and balance over a period
GET ${base}/v1/usage?group_by=day # the same, cut by UTC day, for invoice lines
GET ${base}/v1/account            # status and your two limits`}</Snippet>
      </div>

      <div className="panel">
        <h3>The specification</h3>
        <p>
          The contract this build serves, as OpenAPI — point a client generator at it rather than
          writing requests by hand.
        </p>
        <Snippet>{`${base}/v1/openapi.json`}</Snippet>
      </div>

      <div className="panel">
        <h3>Verifying a webhook</h3>
        <p>
          Deliveries carry <span className="mono">X-Timestamp</span> and{' '}
          <span className="mono">X-Signature</span>. Compute the same HMAC over the timestamp and
          the raw body, compare in constant time, and refuse a timestamp that is not recent —
          otherwise a captured call can be replayed at you forever.
        </p>
        <Snippet>{`signature = "sha256=" + hex(hmac_sha256(secret, timestamp + "." + raw_body))`}</Snippet>
        <p className="muted small">
          If the secret ever leaks, replace it on the Webhook page — the old one stops signing
          immediately. The same page has a button that posts a signed test event, so you can check your
          verifier without spending a generation. A delivery is retried six times over roughly half
          an hour. After that the generation is
          still there to fetch: the webhook is a convenience, the API is the record.
        </p>
      </div>

      <div className="panel">
        <h3>Errors worth handling</h3>
        <table>
          <tbody>
            <tr>
              <td className="mono">401</td>
              <td>Key missing, revoked, or used from an address it is not allowed from.</td>
            </tr>
            <tr>
              <td className="mono">402</td>
              <td>Not enough credits. Top up; nothing was charged.</td>
            </tr>
            <tr>
              <td className="mono">403</td>
              <td>The account is suspended. The keys are intact.</td>
            </tr>
            <tr>
              <td className="mono">429</td>
              <td>
                Over one of your two limits — requests a second, or generations in flight. Back off
                and retry.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
